import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  AlertTriangle, Clock, Phone, RefreshCw, Users, CheckCircle,
  ArrowRight, TrendingUp, Target, Zap, BarChart3
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import Layout from "../components/Layout";
import { API } from "../App";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const EscalationDashboardPage = () => {
  const [escalationData, setEscalationData] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [escRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/routing/escalations`),
        axios.get(`${API}/routing/analytics`),
      ]);
      setEscalationData(escRes.data);
      setAnalyticsData(analyticsRes.data);
    } catch (err) {
      toast.error("Failed to load escalation data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleReassign = async (leadId) => {
    try {
      await axios.post(`${API}/routing/route/${leadId}`);
      toast.success("Lead re-routed successfully");
      fetchData();
    } catch (err) {
      toast.error("Failed to re-route lead");
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6 max-w-7xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-slate-200 rounded w-64"></div>
            <div className="grid grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-slate-200 rounded-lg"></div>)}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const esc = escalationData || { stale_leads: [], uncontacted_leads: [], overdue_tasks: [], recent_escalations: [], summary: {} };
  const analytics = analyticsData || { sla_compliance: 100, sla_breached: 0, funnel: [], team_stats: [], by_rule: [], by_method: [], by_source: [], total_routed: 0 };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="escalation-dashboard-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Escalation & Routing Dashboard</h1>
            <p className="text-sm text-slate-500 mt-1">Monitor SLA compliance, stale leads, and routing performance</p>
          </div>
          <Button variant="outline" onClick={fetchData} data-testid="refresh-dashboard-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className={esc.summary.uncontacted_count > 0 ? 'border-red-200 bg-red-50/30' : ''}>
            <CardContent className="p-4 text-center">
              <Phone className={`w-5 h-5 mx-auto mb-1 ${esc.summary.uncontacted_count > 0 ? 'text-red-500' : 'text-slate-400'}`} />
              <p className="text-2xl font-bold text-slate-900" data-testid="uncontacted-count">{esc.summary.uncontacted_count || 0}</p>
              <p className="text-xs text-slate-500">Uncontacted (&gt;2h)</p>
            </CardContent>
          </Card>
          <Card className={esc.summary.stale_count > 0 ? 'border-amber-200 bg-amber-50/30' : ''}>
            <CardContent className="p-4 text-center">
              <Clock className={`w-5 h-5 mx-auto mb-1 ${esc.summary.stale_count > 0 ? 'text-amber-500' : 'text-slate-400'}`} />
              <p className="text-2xl font-bold text-slate-900" data-testid="stale-count">{esc.summary.stale_count || 0}</p>
              <p className="text-xs text-slate-500">Stale (&gt;24h)</p>
            </CardContent>
          </Card>
          <Card className={esc.summary.overdue_count > 0 ? 'border-orange-200 bg-orange-50/30' : ''}>
            <CardContent className="p-4 text-center">
              <AlertTriangle className={`w-5 h-5 mx-auto mb-1 ${esc.summary.overdue_count > 0 ? 'text-orange-500' : 'text-slate-400'}`} />
              <p className="text-2xl font-bold text-slate-900" data-testid="overdue-count">{esc.summary.overdue_count || 0}</p>
              <p className="text-xs text-slate-500">Overdue Tasks</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Target className="w-5 h-5 mx-auto mb-1 text-emerald-500" />
              <p className="text-2xl font-bold text-slate-900" data-testid="sla-compliance">{analytics.sla_compliance}%</p>
              <p className="text-xs text-slate-500">SLA Compliance</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Zap className="w-5 h-5 mx-auto mb-1 text-blue-500" />
              <p className="text-2xl font-bold text-slate-900" data-testid="total-routed">{analytics.total_routed}</p>
              <p className="text-xs text-slate-500">Total Routed</p>
            </CardContent>
          </Card>
        </div>

        {/* SLA Progress */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-slate-700">SLA Compliance (First Contact &lt; 2 hours)</p>
              <span className={`text-sm font-bold ${analytics.sla_compliance >= 90 ? 'text-emerald-600' : analytics.sla_compliance >= 70 ? 'text-amber-600' : 'text-red-600'}`}>
                {analytics.sla_compliance}%
              </span>
            </div>
            <Progress value={analytics.sla_compliance} className="h-2" />
            {analytics.sla_breached > 0 && (
              <p className="text-xs text-red-500 mt-1">{analytics.sla_breached} lead(s) breached SLA</p>
            )}
          </CardContent>
        </Card>

        <Tabs defaultValue="escalations" className="space-y-4">
          <TabsList>
            <TabsTrigger value="escalations">Escalations</TabsTrigger>
            <TabsTrigger value="teams">Team Performance</TabsTrigger>
            <TabsTrigger value="funnel">Routing Funnel</TabsTrigger>
            <TabsTrigger value="rules">Rule Performance</TabsTrigger>
          </TabsList>

          {/* Escalations Tab */}
          <TabsContent value="escalations" className="space-y-4">
            {/* Uncontacted Leads */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Phone className="w-4 h-4 text-red-500" /> Uncontacted Leads ({esc.uncontacted_leads.length})
                </CardTitle>
                <p className="text-xs text-slate-400">Leads that haven't received any call/interaction since assignment. Older entries are higher priority.</p>
              </CardHeader>
              <CardContent className="p-0">
                {esc.uncontacted_leads.length === 0 ? (
                  <div className="p-6 text-center text-sm text-emerald-600 flex items-center justify-center gap-2">
                    <CheckCircle className="w-4 h-4" /> All leads contacted within SLA
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Lead</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead>Assigned To</TableHead>
                        <TableHead>Waiting</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {esc.uncontacted_leads.map((lead) => (
                        <TableRow key={lead.lead_id} data-testid={`uncontacted-${lead.lead_id}`}>
                          <TableCell>
                            <button className="text-sm text-blue-600 hover:underline font-medium" onClick={() => navigate(`/leads/${lead.lead_id}`)}>
                              {lead.name}
                            </button>
                            <p className="text-xs text-slate-400">{lead.phone}</p>
                          </TableCell>
                          <TableCell className="text-sm">{lead.source || "—"}</TableCell>
                          <TableCell className="text-sm">{lead.assigned_to_name}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-red-100 text-red-700 text-xs">
                              {lead.hours_waiting}h waiting
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button size="sm" variant="outline" onClick={() => handleReassign(lead.lead_id)} data-testid={`reroute-${lead.lead_id}`}>
                              <RefreshCw className="w-3 h-3 mr-1" /> Re-route
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Stale Leads */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="w-4 h-4 text-amber-500" /> Stale Leads — No Update &gt;24h ({esc.stale_leads.length})
                </CardTitle>
                <p className="text-xs text-slate-400">Leads with no interaction, note, or stage change in over 24 hours. These may need a follow-up.</p>
              </CardHeader>
              <CardContent className="p-0">
                {esc.stale_leads.length === 0 ? (
                  <div className="p-6 text-center text-sm text-emerald-600 flex items-center justify-center gap-2">
                    <CheckCircle className="w-4 h-4" /> All leads are up to date
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Lead</TableHead>
                        <TableHead>Stage</TableHead>
                        <TableHead>Assigned To</TableHead>
                        <TableHead>Stale For</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {esc.stale_leads.map((lead) => (
                        <TableRow key={lead.lead_id} data-testid={`stale-${lead.lead_id}`}>
                          <TableCell>
                            <button className="text-sm text-blue-600 hover:underline font-medium" onClick={() => navigate(`/leads/${lead.lead_id}`)}>
                              {lead.name}
                            </button>
                          </TableCell>
                          <TableCell><Badge variant="outline" className="text-xs">{lead.stage}</Badge></TableCell>
                          <TableCell className="text-sm">{lead.assigned_to_name}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`text-xs ${lead.hours_stale > 48 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                              {lead.hours_stale}h stale
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button size="sm" variant="outline" onClick={() => handleReassign(lead.lead_id)}>
                              <RefreshCw className="w-3 h-3 mr-1" /> Re-route
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Overdue Tasks */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-orange-500" /> Overdue Tasks ({esc.overdue_tasks.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {esc.overdue_tasks.length === 0 ? (
                  <div className="p-6 text-center text-sm text-emerald-600 flex items-center justify-center gap-2">
                    <CheckCircle className="w-4 h-4" /> No overdue tasks
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Task</TableHead>
                        <TableHead>Lead</TableHead>
                        <TableHead>Assigned To</TableHead>
                        <TableHead>Due Date</TableHead>
                        <TableHead>Priority</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {esc.overdue_tasks.map((task) => (
                        <TableRow key={task.task_id} data-testid={`overdue-task-${task.task_id}`}>
                          <TableCell className="text-sm font-medium">{task.title}</TableCell>
                          <TableCell>
                            {task.lead_id && (
                              <button className="text-sm text-blue-600 hover:underline" onClick={() => navigate(`/leads/${task.lead_id}`)}>
                                {task.lead_id.slice(0, 12)}...
                              </button>
                            )}
                          </TableCell>
                          <TableCell className="text-sm">{task.assigned_to_name}</TableCell>
                          <TableCell className="text-xs text-slate-500">{task.due_date ? new Date(task.due_date).toLocaleDateString() : "—"}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`text-xs ${task.priority === 'high' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`}>
                              {task.priority}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Team Performance Tab */}
          <TabsContent value="teams" className="space-y-4">
            {analytics.team_stats.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center text-slate-400">
                  <Users className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p>No team data available. Create teams and route leads to see performance.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {analytics.team_stats.map((team, i) => (
                  <Card key={i} data-testid={`team-stat-${team.team_name}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base">{team.team_name}</CardTitle>
                        <Badge variant="outline" className="text-xs">{team.team_type}</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="grid grid-cols-4 gap-2 text-center">
                        <div>
                          <p className="text-lg font-bold text-slate-900">{team.total_leads}</p>
                          <p className="text-xs text-slate-500">Total</p>
                        </div>
                        <div>
                          <p className="text-lg font-bold text-emerald-600">{team.booked}</p>
                          <p className="text-xs text-slate-500">Booked</p>
                        </div>
                        <div>
                          <p className="text-lg font-bold text-red-600">{team.lost}</p>
                          <p className="text-xs text-slate-500">Lost</p>
                        </div>
                        <div>
                          <p className="text-lg font-bold text-blue-600">{team.members}</p>
                          <p className="text-xs text-slate-500">Members</p>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-slate-500">Conversion Rate</span>
                          <span className="text-xs font-bold text-slate-700">{team.conversion_rate}%</span>
                        </div>
                        <Progress value={team.conversion_rate} className="h-1.5" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Funnel Tab */}
          <TabsContent value="funnel" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Lead Routing Funnel</CardTitle>
                <p className="text-xs text-slate-400 mt-1">Number of leads at each pipeline stage. A healthy funnel should narrow progressively from 'new' to 'booked'.</p>
              </CardHeader>
              <CardContent>
                {analytics.funnel.length === 0 ? (
                  <p className="text-center text-slate-400 py-8">No funnel data available</p>
                ) : (
                  <div className="space-y-2">
                    {analytics.funnel.map((stage, i) => {
                      const max = Math.max(...analytics.funnel.map(f => f.count), 1);
                      const pct = (stage.count / max) * 100;
                      const stageColors = {
                        new: "bg-blue-500", contacted: "bg-cyan-500", qualified: "bg-emerald-500",
                        site_visit_scheduled: "bg-amber-400", site_visit: "bg-violet-500", revisit: "bg-fuchsia-500", negotiation_scheduled: "bg-teal-500", negotiation: "bg-amber-500", booked: "bg-green-600", re_engaged: "bg-orange-500"
                      };
                      return (
                        <div key={stage.stage} className="flex items-center gap-3" data-testid={`funnel-${stage.stage}`}>
                          <span className="text-sm text-slate-600 w-24 capitalize">{stage.stage.replace(/_/g, ' ')}</span>
                          <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden">
                            <div className={`h-full rounded-full ${stageColors[stage.stage] || 'bg-slate-400'} transition-all flex items-center justify-end pr-2`}
                              style={{ width: `${Math.max(pct, 5)}%` }}>
                              <span className="text-xs font-bold text-white">{stage.count}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Source Performance */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Source Performance</CardTitle>
                <p className="text-xs text-slate-400 mt-1">Lead volume and conversion rate by source. Conversion = leads that reached 'booked' stage from that source.</p>
              </CardHeader>
              <CardContent className="p-0">
                {analytics.by_source.length === 0 ? (
                  <p className="text-center text-slate-400 py-8">No source data</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Source</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="text-right">Contacted</TableHead>
                        <TableHead className="text-right">Booked</TableHead>
                        <TableHead className="text-right">Conv. Rate</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {analytics.by_source.map((s, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-medium capitalize">{s.source}</TableCell>
                          <TableCell className="text-right">{s.total}</TableCell>
                          <TableCell className="text-right">{s.contacted}</TableCell>
                          <TableCell className="text-right">{s.booked}</TableCell>
                          <TableCell className="text-right">
                            <span className={s.total > 0 && s.booked > 0 ? 'text-emerald-600 font-medium' : 'text-slate-400'}>
                              {s.total > 0 ? ((s.booked / s.total) * 100).toFixed(1) : 0}%
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Rule Performance Tab */}
          <TabsContent value="rules" className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Routing by Rule</CardTitle>
                </CardHeader>
                <CardContent>
                  {analytics.by_rule.length === 0 ? (
                    <p className="text-center text-slate-400 py-6">No routing data yet</p>
                  ) : (
                    <div className="space-y-2">
                      {analytics.by_rule.map((r, i) => (
                        <div key={i} className="flex items-center justify-between py-1.5 border-b last:border-0">
                          <span className="text-sm text-slate-700">{r.rule}</span>
                          <Badge variant="secondary">{r.count} leads</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Assignment Methods Used</CardTitle>
                </CardHeader>
                <CardContent>
                  {analytics.by_method.length === 0 ? (
                    <p className="text-center text-slate-400 py-6">No assignment data yet</p>
                  ) : (
                    <div className="space-y-2">
                      {analytics.by_method.map((m, i) => (
                        <div key={i} className="flex items-center justify-between py-1.5 border-b last:border-0">
                          <span className="text-sm text-slate-700 capitalize">{m.method.replace(/_/g, ' ')}</span>
                          <Badge variant="secondary">{m.count} leads</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default EscalationDashboardPage;
